import React, { useEffect, useState } from 'react'
import { Col, Row, Spin } from 'antd'
import {
	UserAddOutlined,
	WechatOutlined,
	FireOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'

import { useStores } from '../../../cls/stores/MobXProvider'
import { getCardData } from '../../../api/modules/api-dashboard'
import DashboardCard from './DashboardCard'
import Proptypes from 'prop-types'
import { responseHasError } from '../../../stores/users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
/**
 * @component CardData
 * @description
 * Displays a dynamic dashboard summary row with metric cards for key statistics like:
 * - Active Sprints
 * - Pending Sprints
 * - Unresolved Requests
 * - Pending Recruitment (for Super Admin)
 * - Total Worked Hours (for specific user)

 * The component fetches data from the backend via `getCardData()` using provided parameters
 * and renders Ant Design cards (`<DashboardCard>`) with icon visuals and optional loading spinners.

 * @param {Object} props
 * @param {Object} props.params - Parameters passed to the backend API for filtering data.
 * @param {string} [props.params.user_id] - User ID to fetch user-specific metrics (e.g., hours worked).
 * @param {string} [props.params.frequency] - Optional time range indicator (e.g., "weekly", "monthly").

 * @features
 * - Conditionally shows cards based on user role (e.g., Super Admin sees recruitment metrics).
 * - Displays loading spinner (`<Spin />`) during async fetches.
 * - Supports translated labels using `react-i18next`.
 * - Uses Ant Design icons and layout system for responsiveness.
 * - Wraps async data fetch using `wrapWithLoading` for optional global loader tracking.

 * @example
 * <CardData params={{ user_id: '123', frequency: 'monthly' }} />

 * @returns {React.ReactElement} A responsive `<Row>` of dashboard metric cards.
 */
const CardData = ({ params }) => {
	const navigate = useNavigate()

	const { users } = useStores()
	const { loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const { user_id, frequency } = params

	const [cardData, setCardData] = useState({})
	const [loadingCardData, setLoadingCardData] = useState(false)
	useEffect(() => {
		const fetchData = wrapWithLoading(
			async () => {
				// Set loading state
				setLoadingCardData(true)

				try {
					// Fetch card data from the server
					const cardResponse = await getCardData(params)
					// Check for errors in the response
					const hasError = responseHasError(cardResponse)
					if (!hasError && cardResponse.status === 'success') {
						// Update state with fetched data
						setCardData(cardResponse || {})
					} else {
						throw cardResponse
					}
				} catch (error) {
					console.error('Failed to fetch card data:', error)
				} finally {
					// Ensure loading state is reset
					setLoadingCardData(false)
				}
			},
			{ loadingMessage: 'Loading Card Data' }
		)
		fetchData().catch(console.error)
	}, [user_id, frequency])

	const colParams = isMobile ? { span: 12 } : {}
	return (
		<Row
			gutter={[16, 16]}
			justify={'start'}
			align="middle"
			className="dashboard-card-data"
		>
			<Col {...colParams}>
				<DashboardCard
					onClick={() => navigate('/sprints')}
					title="Active Sprints"
					count={
						loadingCardData ? (
							<Spin />
						) : (
							cardData.total_active_sprints
						)
					}
					icon={<FireOutlined style={{ fontSize: 30 }} />}
				/>
			</Col>
			<Col {...colParams}>
				<DashboardCard
					onClick={() => navigate('/sprints')}
					title="Pending Sprints"
					count={
						loadingCardData ? (
							<Spin />
						) : (
							cardData.total_pending_sprints
						)
					}
					icon={<FireOutlined style={{ fontSize: 30 }} />}
				/>
			</Col>
			<Col {...colParams}>
				<DashboardCard
					onClick={() => navigate('/requests')}
					title="Unresolved Requests"
					count={
						loadingCardData ? (
							<Spin />
						) : (
							cardData.total_unresolved_request
						)
					}
					icon={<WechatOutlined style={{ fontSize: 30 }} />}
				/>
			</Col>
			{isSuperAdmin && (
				<Col {...colParams}>
					<DashboardCard
						onClick={() => navigate('/recruitment')}
						title="Pending Recruitment"
						count={
							loadingCardData ? (
								<Spin />
							) : (
								cardData.total_pending_recruitment
							)
						}
						icon={<UserAddOutlined style={{ fontSize: 30 }} />}
					/>
				</Col>
			)}
		</Row>
	)
}

CardData.propTypes = {
	params: Proptypes.object
}

export default CardData
